import React from 'react';
import _ from 'lodash';
import { string, number, func } from 'prop-types';
import { Input, SegmentedToggle } from 'wix-style-react';
import CurrencyUtils from '@wix/wixrest-utils/dist/CurrencyUtils';
import { DiscountUnits } from '../../utils/constants';
import { currencyToString } from '../../utils/CheckoutUtils';
import styles from './DiscountInput.scss';

function clampPercentage(n) {
  return Math.min(Math.max(n, 0), 100000);
}

function parseValue({ value, currency, locale, unit }) {
  const percentValue = value / 1000;
  const currencyValue = currencyToString(value, currency, locale, false);

  return unit === DiscountUnits.CURRENCY ? currencyValue : percentValue;
}

function getNumericValue({ stringValue, unit }) {
  const isPercent = unit === DiscountUnits.PERCENT;
  const multiplier = isPercent ? 100 : 10;
  const result = 10 * Number(stringValue) * multiplier;
  return isPercent ? clampPercentage(result) : result;
}

function addToValue({ value, unit, toAdd }) {
  if (unit === DiscountUnits.PERCENT) {
    return clampPercentage(value + toAdd * 1000);
  } else if (unit === DiscountUnits.CURRENCY) {
    return Math.max(value + toAdd * 100, 0);
  }
}

export default class DiscountInput extends React.Component {
  constructor(props) {
    super(props);
    const { value, currency, locale, unit } = props;
    const localStringValue = parseValue({ value, currency, locale, unit });

    this.state = {
      localStringValue,
    };
  }

  static getDerivedStateFromProps(props, state) {
    const { value: numericValue, currency, locale, unit } = props;
    const newLocalStringValue = parseValue({ value: numericValue, currency, locale, unit });
    const newNumericValue = getNumericValue({ stringValue: state.localStringValue, unit });

    if (newNumericValue !== numericValue && _.last(state.localStringValue) !== '.') {
      return { localStringValue: newLocalStringValue };
    } else {
      return null;
    }
  }

  handleInputChange = (event) => {
    const { value } = event.target;
    const { onChange, unit } = this.props;

    if (!isNaN(value)) {
      const newNumberValue = getNumericValue({ stringValue: value, unit });
      const currentNumberValue = getNumericValue({ stringValue: this.state.localStringValue, unit });
      if (newNumberValue !== currentNumberValue) {
        onChange(newNumberValue);
      }
      this.setState({ localStringValue: value });
    }
  };

  handleInputBlur = () => {
    const { localStringValue } = this.state;
    const { currency, locale, unit } = this.props;
    const newNumberValue = getNumericValue({ stringValue: localStringValue, unit });
    const parsedStringValue = parseValue({ value: newNumberValue, currency, locale, unit });
    this.setState({ localStringValue: parsedStringValue });
  };

  handleKeyDown = (event) => {
    const { onChange, value, unit } = this.props;

    if (event.key === 'ArrowUp') {
      onChange(addToValue({ value, unit, toAdd: 1 }));
    } else if (event.key === 'ArrowDown') {
      onChange(addToValue({ value, unit, toAdd: -1 }));
    }
  };

  render() {
    const { currency, unit, onUnitChange } = this.props;
    const currencySymbol = CurrencyUtils.currencySymbol(currency);
    const { localStringValue } = this.state;
    const prefix = (
      <Input.Affix data-hook="discount-input-prefix">
        {unit === DiscountUnits.PERCENT ? '%' : currencySymbol}
      </Input.Affix>
    );

    return (
      <div className={styles.discountWrapper}>
        <Input
          className={styles.discountInput}
          dataHook="discount-input-component"
          value={localStringValue}
          onChange={this.handleInputChange}
          onBlur={this.handleInputBlur}
          onKeyDown={this.handleKeyDown}
          prefix={prefix}
        />
        <SegmentedToggle
          defaultSelected={unit}
          onClick={(evt, value) => onUnitChange(value)}
          dataHook="discount-type-component"
        >
          <SegmentedToggle.Icon
            className={styles.segmentedToggle}
            value={DiscountUnits.CURRENCY}
            tooltipText={currency}
            dataHook="discount-type-component-currency"
          >
            {currencySymbol}
          </SegmentedToggle.Icon>
          <SegmentedToggle.Icon
            className={styles.segmentedToggle}
            value={DiscountUnits.PERCENT}
            tooltipText="Percentage"
            dataHook="discount-type-component-percentage"
          >
            %
          </SegmentedToggle.Icon>
        </SegmentedToggle>
      </div>
    );
  }
}

DiscountInput.propTypes = {
  currency: string,
  locale: string,
  value: number,
  unit: string,
  onChange: func,
  onUnitChange: func,
};
